/**
 * Custom error class for API errors that includes HTTP status code information.
 * Useful for making intelligent retry decisions in React Query hooks.
 *
 * @extends Error
 */
export class ApiError extends Error {
    /**
     * @param {string} message - Error message
     * @param {number|null} statusCode - HTTP status code
     * @param {*} [data] - Additional error data from the API response
     */
    constructor(message, statusCode, data = null) {
        super(message);
        this.name = 'ApiError';
        this.statusCode = statusCode;
        this.data = data;
    }

    /**
     * Returns true if the error is a client error (4xx status code)
     * @returns {boolean}
     */
    get isClientError() {
        return this.statusCode >= 400 && this.statusCode < 500;
    }

    /**
     * Returns true if the error is a server error (5xx status code)
     * @returns {boolean}
     */
    get isServerError() {
        return this.statusCode >= 500;
    }

    /**
     * Returns true if the error is an authentication/authorization error (401 or 403)
     * @returns {boolean}
     */
    get isAuthError() {
        return this.statusCode === 401 || this.statusCode === 403;
    }

    /**
     * Returns true if the error is a network/unknown error (no status code)
     * @returns {boolean}
     */
    get isNetworkError() {
        return this.statusCode === null || this.statusCode === undefined;
    }
}
